import isUrl from "is-url"
import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { mimeTypeString, PropertyId, PropertySchema, PropertyStatus, stringToMimeType } from "~/classes/entity"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendFileCreateRequest } from "~/classes/handlers/file/create"

class Clipboard extends GlobalComposable<{
  action?: "cut" | "copy"
  files: Record<string, StaticFile>
}> {
  override defaultState = () => ({ files: {} })

  hasClipboardFiles = () => {
    return len(this.state.value.files) > 0
  }

  onFilesCopied = async (files: Record<string, StaticFile>) => {
    return this._onYanked("copy", files)
  }

  hasCopiedFiles = () => {
    return this.state.value.action === "copy" && this.hasClipboardFiles()
  }

  onFilesCut = async (files: Record<string, StaticFile>) => {
    return this._onYanked("cut", files)
  }

  hasCutFiles = () => {
    return this.state.value.action === "cut" && this.hasClipboardFiles()
  }

  _onYanked = async (action: "cut" | "copy", files: Record<string, StaticFile>) => {
    this.state.value.action = action
    this.state.value.files = files
    const html = Object.values(files).map(this.asClipboardHtml)
    const type = "text/html"
    const blob = new Blob(html, { type })
    await navigator.clipboard.write([
      new ClipboardItem({ [type]: blob }),
    ])
  }

  pasteManually = async () => {

  }

  onPasted = async (data: DataTransfer, destination: StaticFile) => {
    const { copyFiles, onWebFilesDropped } = useJobs()
    const { moveFiles } = useFileSystem()

    // check if we are uploading a link type
    for (const type of data.types) {
      if (type === "text/plain") {
        try {
          const { file, preHash } = await this.parsePastedUrl(data.getData(type), destination)
          return sendFileCreateRequest({
            requestId: generateWorkerRequestId(),
            file: file.inner(),
            offline: false,
            preHash,
          })
        }
        // eslint-disable-next-line unused-imports/no-unused-vars
        catch (error) { }
      }
    }

    if (data.files.length || !this.state.value.action)
      return onWebFilesDropped(data, destination)

    // Take the files from the paste event, parse them to see if they are
    // usable by us.
    const files: Record<string, StaticFile> = {}
    for (const type of data.types) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(data.getData(type), "text/html")
      for (const node of doc.body.children as unknown as HTMLElement[]) {
        const parsed = this.parseClipboardHtml(node)
        if (parsed)
          files[parsed.id] = parsed
        else
          this.error("Could not parse node as file", node)
      }
    }

    console.debug("Parsed files", files)

    if (!isDefined(this.state.value.action))
      this.warn("Pasting from unknown location, default to copy")

    if (this.state.value.action === "cut")
      return moveFiles(files, destination)
    else
      return copyFiles(files, destination)
  }

  asClipboardHtml = (file: StaticFile) => {
    const mimeType = maybe(mimeTypeString)(file.mimeType())
    const parent = file.property(PropertyId.FILE_PARENT_ID)
    if (!mimeType || !parent)
      throw new Error("No mime type or parent, can't add to clipboard")

    if (file.isImage())
      return `<img 
      src="${file.thumbnail64Url()}" 
      data-file-id="${file.id}"
      data-mime-type="${mimeType}"
      data-file-size="${file.size()}"
      data-parent-id="${file.inferredParentId()}" 
      data-parent-version="${parent.version}"
    >`
    else if (file.isLink())
      return `<a 
      href="${file.linkDestinationUrl()}" 
      data-file-id="${file.id}"
      data-mime-type="${mimeType}"
      data-parent-id="${file.inferredParentId()}" 
      data-parent-version="${parent.version}"
    >
      ${file.name()}
    </a>`
    else if (file.isFolder())
      return `<div 
      data-file-id="${file.id}" 
      data-mime-type="${mimeType}"
      data-parent-id="${file.inferredParentId()}" 
      data-folder-usage-size="${file.folderUsageSize()}"
      data-parent-version="${parent.version}"
    >
      ${file.name()}
    </div>`
    else return `<div 
      data-file-id="${file.id}" 
      data-mime-type="${mimeType}" 
      data-file-size="${file.size()}"
      data-parent-id="${file.inferredParentId()}"
      data-parent-version="${parent.version}"
    >
      ${file.name()}
    </div>`
  }

  /**
   * TODO: base 64 encode the file as a datagram instead of adding these
   * properties, which will make the entire thing better
   */
  parsePastedUrl = async (url: string, destination: StaticFile) => {
    const { state: myself } = useSelf()

    url = url.trim()
    if (!url.startsWith("http://") && !url.startsWith("https://"))
      url = `http://${url}`

    if (url.startsWith("file://"))
      throw new Error("Local file links are not supported")
    if (!isUrl(url))
      throw new Error(`Not a url: ${url}`)

    if (!myself.value.user?.id) {
      this.error("Cannot create a folder as an anon user yet")
      throw new Error("Cannot create urls as anon")
    }

    const preHash = StaticFile.clientPreHashId()
    const fileId = await StaticFile.idFromPrehash(preHash)
    const userId = myself.value.user!.id
    const parentId = destination.id
    return { file: await StaticFile.newLink({ id: fileId, userId, parentId, url }), preHash }
  }

  parseClipboardHtml = (element: HTMLElement): StaticFile | undefined => {
    const { state: myself } = useSelf()

    const mimeTypeString = element.dataset.mimeType
    const fileId = element.dataset.fileId
    const sizeBytes = maybe(Number.parseInt)(element.dataset.fileSize)
    const folderUsage = maybe(Number.parseInt)(element.dataset.folderUsageSize)
    const parentId = element.dataset.parentId
    const version = Number.parseInt(element.dataset.parentVersion || "0")

    if (!myself.value.user || !mimeTypeString || !fileId || !parentId) {
      this.error("Cannot parse this element without a mime type or file id")
      return
    }
    const file = StaticFile.blank(fileId)
    const mimeType = stringToMimeType(mimeTypeString)
    const timestamp = uuidv7()

    // We add the parent property to the object so that when we make the
    // move request we have the right version
    file.addProperty({
      id: file.id,
      propertyId: PropertyId.FILE_MIME_TYPE,
      version: 0,
      schema: PropertySchema.MimeTypeValue,
      value: { type: PropertySchema.MimeTypeValue, mimeType },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: myself.value.user.id,
    })
    file.addProperty({
      id: file.id,
      propertyId: PropertyId.FILE_PARENT_ID,
      version,
      schema: PropertySchema.FileIdValue,
      value: { type: PropertySchema.FileIdValue, fileId: parentId },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: myself.value.user.id,
    })
    if (isDefined(sizeBytes))
      file.addProperty({
        id: file.id,
        propertyId: PropertyId.FILE_SIZE,
        version: 0,
        schema: PropertySchema.SizeBytesValue,
        value: { type: PropertySchema.SizeBytesValue, sizeBytes },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: myself.value.user.id,
      })
    if (isDefined(folderUsage))
      file.addProperty({
        id: file.id,
        propertyId: PropertyId.FOLDER_USAGE_SIZE,
        version: 0,
        schema: PropertySchema.SizeBytesValue,
        value: { type: PropertySchema.SizeBytesValue, sizeBytes: folderUsage },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: myself.value.user.id,
      })

    return file
  }

  /** Copy a string to the clipboard */
  copyTextToClipboard = async (text: string) => {
    // replace nbsp with a normal space
    text = text.replace(/\u00A0/g, " ")
    await navigator.clipboard.writeText(text)
  }
}

const clipboard = new Clipboard("clipboard", theme.colors.amber.hex)
export default function () {
  return clipboard.use()
}
